import { Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DeleteConfirmDialogProps<T> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: T[];
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
  title?: string;
  description?: string;
  entityName: string; // "theme", "project", "file", etc.
  getItemLabel: (item: T) => string; // Function to get display name from item
  maxDisplayItems?: number; // Max items to show in list (default 10)
}

export function DeleteConfirmDialog<T>({
  open,
  onOpenChange,
  items,
  onConfirm,
  isDeleting,
  title,
  description,
  entityName,
  getItemLabel,
  maxDisplayItems = 10,
}: DeleteConfirmDialogProps<T>): JSX.Element {
  const count = items.length;
  const plural = count === 1 ? entityName : `${entityName}s`;
  const defaultTitle = `Delete ${count} ${plural}?`;
  const defaultDescription = `You are about to permanently delete the following ${plural}:`;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title || defaultTitle}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>{description || defaultDescription}</p>

            {/* Items list (max N items) */}
            <ul className="list-disc list-inside space-y-1 text-foreground font-medium">
              {items.slice(0, maxDisplayItems).map((item, index) => (
                <li key={index}>{getItemLabel(item)}</li>
              ))}
              {count > maxDisplayItems && (
                <li>and {count - maxDisplayItems} more...</li>
              )}
            </ul>

            <p className="text-destructive font-medium">
              This action cannot be undone.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              `Delete ${count} ${plural}`
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
